import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Form,
  Input,
  Select,
  Switch,
  Space,
  Typography,
  Divider,
  Button,
  Empty,
  Tag,
  Popconfirm,
} from 'antd';
import {
  SettingOutlined,
  BranchesOutlined,
  TableOutlined,
  PlusOutlined,
  CloseOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { selectTemplateData, updateTemplateData } from '../../../../store/slices/rfpTemplateSlice';
import { toggleConditionalBuilder, toggleTableConfig, toggleRepeaterConfig } from '../../../../store/slices/rfpBuilderSlice';
import { produce } from 'immer';

const { Text, Title } = Typography;
const { TextArea } = Input;

const fieldTypeOptions = [
  { label: 'Text Input', value: 'text' },
  { label: 'Text Area', value: 'textarea' },
  { label: 'Number', value: 'number' },
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
  { label: 'URL', value: 'url' },
  { label: 'Dropdown', value: 'select' },
  { label: 'Multi-Select', value: 'multiselect' },
  { label: 'Radio Buttons', value: 'radio' },
  { label: 'Checkboxes', value: 'checkbox' },
  { label: 'Date', value: 'date' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'Time', value: 'time' },
  { label: 'Date Range', value: 'daterange' },
  { label: 'File Upload', value: 'file' },
  { label: 'Rating', value: 'rating' },
  { label: 'Currency', value: 'currency' },
  { label: 'Percentage', value: 'percentage' },
  { label: 'Table/Grid', value: 'table' },
];

export default function PropertiesPanel({ selectedElement }) {
  const dispatch = useDispatch();
  const templateData = useSelector(selectTemplateData);
  const [form] = Form.useForm();
  const [elementData, setElementData] = useState(null);
  const [newOption, setNewOption] = useState('');

  // Parse path to get indices
  const parsePath = (path) => {
    const match = path.match(/sheet-(\d+)(?:-section-(\d+))?(?:-category-(\d+))?(?:-question-(\d+))?/);
    if (!match) return null;

    return {
      sheetIdx: parseInt(match[1]),
      sectionIdx: match[2] !== undefined ? parseInt(match[2]) : null,
      categoryIdx: match[3] !== undefined ? parseInt(match[3]) : null,
      questionIdx: match[4] !== undefined ? parseInt(match[4]) : null,
    };
  };

  // Get element data from template
  useEffect(() => {
    if (!selectedElement || !templateData) {
      setElementData(null);
      return;
    }

    const indices = parsePath(selectedElement.path);
    if (!indices) {
      setElementData(null);
      return;
    }

    const { sheetIdx, sectionIdx, categoryIdx, questionIdx } = indices;

    let data = null;
    if (questionIdx !== null) {
      // Question selected
      data = templateData.sheets?.[sheetIdx]?.sections?.[sectionIdx]?.categories?.[categoryIdx]?.questions?.[questionIdx];
    } else if (categoryIdx !== null) {
      // Category selected
      data = templateData.sheets?.[sheetIdx]?.sections?.[sectionIdx]?.categories?.[categoryIdx];
    } else if (sectionIdx !== null) {
      // Section selected
      data = templateData.sheets?.[sheetIdx]?.sections?.[sectionIdx];
    } else {
      // Sheet selected
      data = templateData.sheets?.[sheetIdx];
    }

    setElementData(data);
    if (data) {
      form.setFieldsValue(data);
    }
  }, [selectedElement, templateData, form]);

  if (!selectedElement || !elementData) {
    return (
      <div style={{ padding: '24px' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" size="small">
              <Text type="secondary">No element selected</Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Select an element to edit properties
              </Text>
            </Space>
          }
        />
      </div>
    );
  }

  const handleFieldChange = (field, value) => {
    const indices = parsePath(selectedElement.path);
    if (!indices) return;

    const { sheetIdx, sectionIdx, categoryIdx, questionIdx } = indices;

    const newData = produce(templateData, (draft) => {
      let target;
      if (questionIdx !== null) {
        target = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions[questionIdx];
      } else if (categoryIdx !== null) {
        target = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx];
      } else if (sectionIdx !== null) {
        target = draft.sheets[sheetIdx].sections[sectionIdx];
      } else {
        target = draft.sheets[sheetIdx];
      }

      target[field] = value;
    });

    dispatch(updateTemplateData(newData));
  };

  const handleAddOption = () => {
    if (!newOption.trim()) return;

    const indices = parsePath(selectedElement.path);
    if (!indices || indices.questionIdx === null) return;

    const { sheetIdx, sectionIdx, categoryIdx, questionIdx } = indices;

    const newData = produce(templateData, (draft) => {
      const question = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions[questionIdx];
      if (!question.options) {
        question.options = [];
      }
      question.options.push({ label: newOption, value: newOption });
    });

    dispatch(updateTemplateData(newData));
    setNewOption('');
  };

  const handleRemoveOption = (index) => {
    const indices = parsePath(selectedElement.path);
    if (!indices || indices.questionIdx === null) return;

    const { sheetIdx, sectionIdx, categoryIdx, questionIdx } = indices;

    const newData = produce(templateData, (draft) => {
      const question = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions[questionIdx];
      question.options.splice(index, 1);
    });

    dispatch(updateTemplateData(newData));
  };

  const handleDelete = () => {
    const indices = parsePath(selectedElement.path);
    if (!indices) return;

    const newData = produce(templateData, (draft) => {
      const { sheetIdx, sectionIdx, categoryIdx, questionIdx } = indices;

      if (questionIdx !== null) {
        // Delete question
        draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions.splice(questionIdx, 1);
      } else if (categoryIdx !== null) {
        // Delete category
        draft.sheets[sheetIdx].sections[sectionIdx].categories.splice(categoryIdx, 1);
      } else if (sectionIdx !== null) {
        // Delete section
        draft.sheets[sheetIdx].sections.splice(sectionIdx, 1);
      } else {
        // Delete sheet
        draft.sheets.splice(sheetIdx, 1);
      }
    });

    dispatch(updateTemplateData(newData));
  };

  const needsOptions = elementData.field_type && ['select', 'multiselect', 'radio', 'checkbox'].includes(elementData.field_type);

  const renderQuestionProperties = () => (
    <Form form={form} layout="vertical" size="small">
      <Form.Item label="Question Text" name="question">
        <TextArea
          rows={3}
          placeholder="Enter question text"
          onChange={(e) => handleFieldChange('question', e.target.value)}
        />
      </Form.Item>

      <Form.Item label="Field Type" name="field_type">
        <Select
          options={fieldTypeOptions}
          placeholder="Select field type"
          disabled
        />
      </Form.Item>

      {needsOptions && (
        <Form.Item label="Options">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {elementData.options?.map((option, index) => (
              <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Tag
                  closable
                  onClose={() => handleRemoveOption(index)}
                  style={{ marginRight: 0, padding: '4px 8px', fontSize: '13px' }}
                >
                  {option.label || option}
                </Tag>
              </div>
            ))}
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Add option"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onPressEnter={handleAddOption}
              />
              <Button icon={<PlusOutlined />} onClick={handleAddOption}>Add</Button>
            </Space.Compact>
          </Space>
        </Form.Item>
      )}

      <Form.Item label="Help Text" name="help_text">
        <Input
          placeholder="Optional help text"
          onChange={(e) => handleFieldChange('help_text', e.target.value)}
        />
      </Form.Item>

      <Form.Item label="Required" name="required" valuePropName="checked">
        <Switch onChange={(checked) => handleFieldChange('required', checked)} />
      </Form.Item>

      <Form.Item label="Read Only" name="read_only" valuePropName="checked">
        <Switch onChange={(checked) => handleFieldChange('read_only', checked)} />
      </Form.Item>

      <Divider />

      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text strong>Advanced Options</Text>

        <Button
          icon={<BranchesOutlined />}
          onClick={() => dispatch(toggleConditionalBuilder())}
          block
        >
          Conditional Logic
        </Button>

        {elementData.field_type === 'table' && (
          <Button
            icon={<TableOutlined />}
            onClick={() => dispatch(toggleTableConfig())}
            block
          >
            Table Configuration
          </Button>
        )}

        {elementData.field_type === 'repeater' && (
          <Button
            icon={<CopyOutlined />}
            onClick={() => dispatch(toggleRepeaterConfig())}
            block
          >
            Repeater Configuration
          </Button>
        )}

        <Divider style={{ margin: '12px 0' }} />

        <Popconfirm
          title="Delete this field?"
          description="This action cannot be undone."
          onConfirm={handleDelete}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button
            danger
            icon={<DeleteOutlined />}
            block
          >
            Delete Field
          </Button>
        </Popconfirm>
      </Space>
    </Form>
  );

  const handleAddSection = () => {
    const indices = parsePath(selectedElement.path);
    if (!indices || indices.sheetIdx === null) return;

    const newData = produce(templateData, (draft) => {
      const sheet = draft.sheets[indices.sheetIdx];
      if (!sheet.sections) {
        sheet.sections = [];
      }
      sheet.sections.push({
        id: `section_${Date.now()}`,
        name: `Section ${sheet.sections.length + 1}`,
        categories: [],
      });
    });
    dispatch(updateTemplateData(newData));
  };

  const handleAddCategory = () => {
    const indices = parsePath(selectedElement.path);
    if (!indices || indices.sectionIdx === null) return;

    const newData = produce(templateData, (draft) => {
      const section = draft.sheets[indices.sheetIdx].sections[indices.sectionIdx];
      if (!section.categories) {
        section.categories = [];
      }
      section.categories.push({
        id: `category_${Date.now()}`,
        name: `Category ${section.categories.length + 1}`,
        questions: [],
      });
    });
    dispatch(updateTemplateData(newData));
  };

  const renderContainerProperties = () => {
    const indices = parsePath(selectedElement.path);
    const isSheet = indices && indices.sectionIdx === null;
    const isSection = indices && indices.sectionIdx !== null && indices.categoryIdx === null;

    return (
      <Form form={form} layout="vertical" size="small">
        <Form.Item label="Name" name="name">
          <Input
            placeholder="Enter name"
            onChange={(e) => handleFieldChange('name', e.target.value)}
          />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea
            rows={3}
            placeholder="Optional description"
            onChange={(e) => handleFieldChange('description', e.target.value)}
          />
        </Form.Item>

        <Form.Item label="Visible" name="visible" valuePropName="checked">
          <Switch
            defaultChecked={elementData.visible !== false}
            onChange={(checked) => handleFieldChange('visible', checked)}
          />
        </Form.Item>

        <Divider />

        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {isSheet && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddSection}
              block
            >
              Add Section
            </Button>
          )}

          {isSection && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              block
            >
              Add Category
            </Button>
          )}

          <Button
            icon={<BranchesOutlined />}
            onClick={() => dispatch(toggleConditionalBuilder())}
            block
          >
            Conditional Logic
          </Button>

          <Divider style={{ margin: '12px 0' }} />

          <Popconfirm
            title={`Delete this ${isSheet ? 'sheet' : isSection ? 'section' : 'category'}?`}
            description="This will delete all contents. This action cannot be undone."
            onConfirm={handleDelete}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              block
            >
              Delete {isSheet ? 'Sheet' : isSection ? 'Section' : 'Category'}
            </Button>
          </Popconfirm>
        </Space>
      </Form>
    );
  };

  return (
    <div style={{ padding: '16px' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Space size="small">
            <SettingOutlined style={{ fontSize: '16px', color: '#8B5CF6' }} />
            <Title level={5} style={{ margin: 0, fontSize: '15px' }}>
              Properties
            </Title>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}>
            {selectedElement.type === 'question' ? 'Question' : 'Container'} Settings
          </Text>
        </div>

        {selectedElement.type === 'question'
          ? renderQuestionProperties()
          : renderContainerProperties()}
      </Space>
    </div>
  );
}
